import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Button, Tooltip, Space, Modal, message, Tag, Select, Input } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { AppDispatch, RootState } from '../../redux/store/store';
import { fetchSucursales, deleteSucursal, setSucursalesPage, setSucursalesLimit } from '../../redux/actions/sucursales/sucursales';
import { fetchEmpresas } from '../../redux/actions/empresas/Empresas';
import CrearSucursalModal from '../modals/sucursales/CreateSucursalModal';
import EditarSucursalModal from '../modals/sucursales/EditSucursalModal';

const { Option } = Select;

interface ColumnItem {
  key: string;
  title: string;
  dataIndex: string;
  width?: string | number;
  ellipsis?: boolean;
  render?: any;
}

const ListaSucursales = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { sucursales, loading, pagination } = useSelector((state: RootState) => state.sucursales);
  const { empresas } = useSelector((state: RootState) => state.empresas);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedSucursalId, setSelectedSucursalId] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedEmpresaId, setSelectedEmpresaId] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (empresas.length === 0) {
      dispatch(fetchEmpresas(1, 10));
    }
  }, [empresas.length]);

  useEffect(() => {
    dispatch(fetchSucursales(pagination.page, pagination.limit, searchText, selectedEmpresaId));
  }, [pagination.page, pagination.limit, searchText, selectedEmpresaId]);

  const handleTableChange = (pagination: any) => {
    dispatch(setSucursalesPage(pagination.current));
    dispatch(fetchSucursales(pagination.current, pagination.pageSize, searchText, selectedEmpresaId));
  };

  const handlePageSizeChange = (current: number, size: number) => {
    dispatch(setSucursalesLimit(size));
    dispatch(fetchSucursales(current, size, searchText, selectedEmpresaId));
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    dispatch(setSucursalesPage(1));
    dispatch(fetchSucursales(1, pagination.limit, value, selectedEmpresaId));
  };

  const handleEmpresaFilter = (value: number | undefined) => {
    setSelectedEmpresaId(value);
    dispatch(setSucursalesPage(1));
    dispatch(fetchSucursales(1, pagination.limit, searchText, value));
  };

  const columns: ColumnItem[] = [
    {
      key: 'item',
      title: 'Item',
      dataIndex: 'id',
      width: '7%',
      ellipsis: true,
      render: (_: any, __: any, index: number) => (pagination.page - 1) * pagination.limit + index + 1
    },
    {
      key: 'nombre',
      title: 'Nombre',
      dataIndex: 'nombre',
      width: '15%',
      ellipsis: true,
      render: (nombre: string) => (
        <Tooltip title={nombre}>
          <span className="truncate block">{nombre}</span>
        </Tooltip>
      ),
    },
    {
      key: 'empresa_nombre',
      title: 'Empresa',
      dataIndex: 'empresa_nombre',
      width: '15%',
      ellipsis: true,
      render: (nombre: string) => (
        <Tooltip title={nombre}>
          <span className="truncate block">{nombre || 'Sin empresa'}</span>
        </Tooltip>
      ),
    },
    {
      key: 'direccion',
      title: 'Dirección',
      dataIndex: 'direccion',
      width: '20%',
      ellipsis: true,
      render: (direccion: string) => (
        <Tooltip title={direccion}>
          <span className="truncate block">{direccion || 'Sin dirección'}</span>
        </Tooltip>
      ),
    },
    {
      key: 'cantidad_usuarios',
      title: 'Usuarios',
      dataIndex: 'cantidad_usuarios',
      width: '8%',
      render: (cantidad: number) => (
        <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-sm">
          {cantidad || 0}
        </span>
      ),
    },
    {
      key: 'link_maps',
      title: 'Mapa',
      dataIndex: 'link_maps',
      width: '10%',
      ellipsis: true,
      render: (link: string) => (
        link ? (
          <a href={link} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline truncate block">
            Ver ubicación
          </a>
        ) : (
          <Tag color="default" className="truncate">Sin ubicación</Tag>
        )
      ),
    },
    {
      key: 'color',
      title: 'Color',
      dataIndex: 'color',
      width: '10%',
      ellipsis: true,
      render: (color: string) =>
        color ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: color,
                display: 'inline-block',
                border: '1px solid #ccc',
              }}
            />
          </div>
        ) : (
          <Tag color="default" className="truncate">Sin color</Tag>
        ),
    },
    {
      key: 'fecha_creacion',
      title: 'Fecha de Creación',
      dataIndex: 'fecha_creacion',
      width: '15%',
      ellipsis: true,
      render: (fecha: string) => (
        <Tooltip title={fecha}>
          <span className="truncate block">{fecha || 'Sin fecha'}</span>
        </Tooltip>
      ),
    },
    {
      key: 'acciones',
      title: 'Acciones',
      dataIndex: 'id',
      width: '10%',
      render: (id: number, record: any) => (
        <Space size="middle">
          <Tooltip title="Editar">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditSucursal(id)}
            />
          </Tooltip>
          <Tooltip title={record.cantidad_usuarios > 0 ? "No se puede eliminar si tiene usuarios asociados" : "Eliminar"}>
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => showDeleteConfirm(id)}
              disabled={record.cantidad_usuarios > 0}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleEditSucursal = (id: number) => {
    setSelectedSucursalId(id);
    setEditModalVisible(true);
  };

  const handleEditModalClose = (refresh: boolean = false) => {
    setEditModalVisible(false);
    setSelectedSucursalId(null);
    if (refresh) {
      dispatch(fetchSucursales(pagination.page, pagination.limit, searchText, selectedEmpresaId));
    }
  };

  const showDeleteConfirm = (id: number) => {
    const sucursal = sucursales.find(suc => suc.id === id);
    if (sucursal && sucursal.cantidad_usuarios > 0) {
      message.error('No se puede eliminar esta sucursal porque tiene usuarios asociados');
      return;
    }

    setSelectedSucursalId(id);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedSucursalId) {
      setDeleteLoading(true);
      try {
        await dispatch(deleteSucursal(selectedSucursalId.toString()));
        setDeleteModalVisible(false);
        dispatch(fetchSucursales(pagination.page, pagination.limit, searchText, selectedEmpresaId));
        message.success('Sucursal eliminada exitosamente');
      } catch (error) {
        console.error('Error al eliminar la sucursal:', error);
        message.error('Error al eliminar la sucursal');
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalVisible(false);
    setSelectedSucursalId(null);
  };

  const handleCreateSucursal = () => {
    setCreateModalVisible(true);
  };

  const handleCreateModalClose = (refresh: boolean = false) => {
    setCreateModalVisible(false);
    if (refresh) {
      dispatch(fetchSucursales(pagination.page, pagination.limit, searchText, selectedEmpresaId));
    }
  };

  return (
    <div className="py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Lista de Sucursales</h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreateSucursal}
        >
          Nueva Sucursal
        </Button>
      </div>

      <div className="flex mb-6 gap-4">
        <div className="w-1/3">
          <Input
            placeholder="Buscar por nombre"
            prefix={<SearchOutlined />}
            onChange={(e) => handleSearch(e.target.value)}
            value={searchText}
            allowClear
          />
        </div>
        <div className="w-1/3">
          <Select
            placeholder="Filtrar por empresa"
            style={{ width: '100%' }}
            onChange={handleEmpresaFilter}
            value={selectedEmpresaId}
            allowClear
          >
            {empresas.map(empresa => (
              <Option key={empresa.id} value={empresa.id}>
                {empresa.nombre}
              </Option>
            ))}
          </Select>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={sucursales}
        rowKey="id"
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total) => `Total ${total} sucursales`,
          onChange: (page, pageSize) => handleTableChange({ current: page, pageSize }),
          onShowSizeChange: handlePageSizeChange
        }}
      />

      <Modal
        title="Confirmar eliminación"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleCancelDelete}
        confirmLoading={deleteLoading}
        okText="Eliminar"
        cancelText="Cancelar"
        okButtonProps={{ danger: true }}
      >
        <p>¿Está seguro que desea eliminar esta sucursal? Esta acción no se puede deshacer.</p>
      </Modal>

      {createModalVisible && (
        <CrearSucursalModal
          visible={createModalVisible}
          onClose={handleCreateModalClose}
        />
      )}

      {editModalVisible && (
        <EditarSucursalModal
          visible={editModalVisible}
          sucursalId={selectedSucursalId}
          onClose={handleEditModalClose}
        />
      )}
    </div>
  );
};

export default ListaSucursales;